<template>
  <div class="modals-wrapper">
    <b-modal
      id="visume_mode"
      ref="visume_mode"
      hide-header
      hide-footer
      centered
      no-close-on-backdrop
      modal-class="vm-modal"
      content-class="vm-modal__content"
      body-class="vm-modal__body"
      @show="resetModal"
      @hidden="closeModal"
    >
      <div class="vm-modal__shell">
        <div
          v-if="$route.path.includes('/jobseeker/register')"
          class="vm-modal__register-bar"
        >
          <p v-if="startRecording" class="vm-modal__register-text">
            Registration completed. Record your visume.
          </p>
          <button
            id="skip-visume"
            type="button"
            class="vm-modal__register-skip"
            v-if="startRecording"
            @click="showConfirmation"
          >
            Skip for now
          </button>
        </div>

        <div v-else class="vm-modal__header">
          <div class="vm-modal__header-text">
            <p class="vm-modal__eyebrow">
              <i class="mdi mdi-video" aria-hidden="true"></i>
              Visume
            </p>
            <h2 class="vm-modal__title">{{ modalTitle }}</h2>
            <p v-if="modalSubtitle" class="vm-modal__subtitle">{{ modalSubtitle }}</p>
          </div>
          <button
            type="button"
            class="vm-modal__close"
            aria-label="Close"
            @click="dismissModal"
            :disabled="isBusy"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
              <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <form ref="addVisume-form" action="">
          <div id="interactive" class="vm-modal__stage">
            <video
              id="video_resume"
              class="vm-modal__video"
              type="video/*"
              controls
              playsinline
              v-if="videoResume"
              :src="$S3_bucket_url + visume"
            ></video>
            <video
              id="video_view"
              class="vm-modal__video vm-modal__video--live"
              autoplay
              muted
              playsinline
              type="video/*"
              v-if="videoView"
            ></video>
            <video
              id="video_preview"
              class="vm-modal__video"
              type="video/*"
              controls
              playsinline
              v-show="videoPreview"
            ></video>

            <div id="time-interval" class="vm-modal__timer">
              <v-icon class="vm-modal__timer-dot" size="14px">{{ $icons.radioboxMarked }}</v-icon>
              <span class="vm-modal__timer-part" id="video_min">00</span>
              <span class="vm-modal__timer-sep">:</span>
              <span class="vm-modal__timer-part" id="video_sec">00</span>
            </div>

            <div id="countdown" class="vm-modal__countdown">
              <div id="countdown-number"></div>
              <svg style="display: none">
                <circle r="18" cx="20" cy="20"></circle>
              </svg>
            </div>
          </div>

          <div class="vm-modal__actions">
            <div class="vm-modal__success" v-if="saveStatus === 'success'" role="status">
              <i class="mdi mdi-check-circle" aria-hidden="true"></i>
              <div class="vm-modal__success-text">
                <strong>Visume saved</strong>
                <p>{{ successMessage }}</p>
              </div>
            </div>

            <div class="vm-modal__error" v-if="visume_error" role="alert">
              {{ visume_err_msg }}
            </div>

            <div class="vm-modal__uploading" v-if="isUploading">
              <i class="mdi mdi-loading mdi-spin" aria-hidden="true"></i>
              <span>{{ uploadStatusMessage }}</span>
            </div>

            <button
              id="startButton"
              type="button"
              class="vm-modal__btn vm-modal__btn--primary"
              @click.prevent="Updating"
              v-if="updateVisume && !isBusy"
            >
              <i class="mdi mdi-refresh" aria-hidden="true"></i>
              Update visume
            </button>
            <p class="vm-modal__hint" v-if="updateVisume && !isBusy">
              Want to improve your introduction? Record a new ~2 minute video about your skills and experience.
              <span>Recruiters can review your visume when shortlisting candidates.</span>
            </p>

            <button
              id="startRecordButton"
              type="button"
              class="vm-modal__btn vm-modal__btn--record"
              @click.prevent="startRecord"
              v-if="startRecording && !isBusy"
            >
              <span class="vm-modal__record-dot" aria-hidden="true"></span>
              Start recording
            </button>
            <p class="vm-modal__hint" v-if="startRecording && !isBusy">
              Tap <strong>Start recording</strong> to enable your camera and microphone.
              <span>Record a ~2 minute video about yourself, your skills, and why a company should hire you.</span>
            </p>

            <button
              id="stpButton"
              type="button"
              class="vm-modal__btn vm-modal__btn--stop"
              @click.prevent="stopRecording"
              v-if="stopRecord"
            >
              <span class="vm-modal__stop-icon" aria-hidden="true"></span>
              Stop recording
            </button>
            <p class="vm-modal__hint" v-if="stopRecord">
              Keep it under 2 minutes. Briefly cover your background, skills, and what you bring to a role.
              <span>Recruiters can review your visume when shortlisting candidates.</span>
            </p>

            <button
              id="checkRecord"
              type="button"
              class="vm-modal__btn vm-modal__btn--outline"
              @click.prevent="recordAgain"
              v-if="checkRecord && !isBusy"
            >
              <i class="mdi mdi-record-circle-outline" aria-hidden="true"></i>
              Record again
            </button>
          </div>
        </form>
      </div>
    </b-modal>

    <b-modal id="confirmation-visume" ref="confirmation-visume" title=" " hide-footer hide-header>
      <div class="vm-modal__confirm">
        <h3 class="vm-modal__confirm-title">Skip visume recording?</h3>
        <p class="vm-modal__confirm-text">Profiles with a visume are shortlisted more often by recruiters.</p>
        <div class="vm-modal__confirm-actions">
          <button type="button" class="vm-modal__btn vm-modal__btn--outline" @click="skipVisume">
            Skip &amp; login
          </button>
          <button type="button" class="vm-modal__btn vm-modal__btn--primary" @click="hideModal">
            Record visume
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="camera-access"
      ref="camera-access-error"
      hide-footer
      hide-header
      centered
      modal-class="vm-modal"
      content-class="vm-modal__content"
      body-class="vm-modal__body"
    >
      <CameraPermissionGuide @close="hideErroModal" @retry="retryCameraAccess" />
    </b-modal>
  </div>
</template>

<script>
import '@/assets/hire.css';
import AwsUpload from "../../utils/s3upload"
import Actions from '../libraries/apiActions.js';
import CameraPermissionGuide from '@/components/CameraPermissionGuide.vue';
import { setVisumeFeedback } from '@/utils/visumeFeedback';


export default {
  name: "VisumeModal",
  components: {
    CameraPermissionGuide,
  },
  props: {
    open: String,
    visume: String,
    email_status: Number,
  },
  data() {
    return {
      apiAction: new Actions(),
      count: 3,
      streamActive: false,
      mediaRecorder: "",
      start: false,
      hasWorkingDevices: false,
      constrains: {
        audio: true,
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      },
      options: {
        audioBitsPerSecond: 128000,
        videoBitsPerSecond: 150000,
        mimeType: "video/webm;codecs=opus,vp8",
      },
      data:{
        resume:''
      },
      stream: "",
      recordedBlobs: [],
      checkRecording: false,
      OverAllVideoCoundownTimer: "",
      sec: 0,
      duration:0,
      min: 0,
      timer_is_on: 0,
      startRecording:false,
      addVideo:false,
      checkRecord:false,
      stopRecord:false,
      videoView:false,
      videoPreview:false,
      videoResume:false,
      updateVisume: false,
      waiting: false,
      waiting_percentage:null,
      visume_error:false,
      visume_err_msg:null,
      saveStatus: 'idle',
      uploadPhase: '',
      savedDuration: 0,
      successReloadTimer: null,
      video_start_time: null,
      video_end_time: null,
      permissionGuideOpen: false,
    };
  },
  computed: {
    isBusy() {
      return this.waiting || this.saveStatus === 'success';
    },
    isUploading() {
      return this.waiting && this.saveStatus !== 'success';
    },
    uploadStatusMessage() {
      if (this.uploadPhase === 'saving') {
        return 'Saving visume to your profile…';
      }
      return 'Uploading your video…';
    },
    successMessage() {
      if (this.savedDuration > 0) {
        return `Your ${this.savedDuration}-second introduction is on your profile. Recruiters can view it when shortlisting.`;
      }
      return 'Your introduction is on your profile. Recruiters can view it when shortlisting.';
    },
    modalTitle() {
      if (this.saveStatus === 'success') return 'Visume saved';
      if (this.isUploading) return 'Uploading visume';
      if (this.stopRecord) return 'Recording in progress';
      if (this.startRecording) return 'Record your visume';
      if (this.updateVisume) return 'Your video profile';
      if (this.checkRecord) return 'Review your recording';
      return 'Visume';
    },
    modalSubtitle() {
      if (this.updateVisume) return 'Preview your current introduction before recording a new one.';
      if (this.startRecording) return 'Aim for about 2 minutes. Camera and microphone access required.';
      if (this.stopRecord) return 'Tap stop when you are done.';
      if (this.isUploading) return 'Please keep this window open until upload finishes.';
      return '';
    },
  },
  beforeDestroy() {
    this.clearSuccessReloadTimer();
  },
  methods: {
    clearSuccessReloadTimer() {
      if (this.successReloadTimer) {
        clearTimeout(this.successReloadTimer);
        this.successReloadTimer = null;
      }
    },
    setSaveError(message) {
      this.waiting = false;
      this.uploadPhase = '';
      this.saveStatus = 'error';
      this.visume_error = true;
      this.visume_err_msg = message;
      this.startRecording = true;
      this.checkRecord = true;
      this.stopRecord = false;
    },
    formatSaveError(res, err) {
      if (res?.message && res.message !== 'Resume Updated') {
        return res.message;
      }
      if (!window.navigator.onLine) {
        return 'Network error. Check your connection and try again.';
      }
      if (err?.message) {
        return 'Could not save your visume. Please try again.';
      }
      return 'Could not save your visume. Please try again.';
    },
    handleSaveSuccess() {
      this.waiting = false;
      this.uploadPhase = '';
      this.saveStatus = 'success';
      this.savedDuration = this.duration;
      this.visume_error = false;
      this.visume_err_msg = null;

      setVisumeFeedback({
        type: 'success',
        duration: this.duration,
      });

      this.clearSuccessReloadTimer();
      this.successReloadTimer = setTimeout(() => {
        if (this.$route.path.includes('/jobseeker/register')) {
          return;
        }
        this.$refs.visume_mode.hide();
      }, 2000);
    },
    dismissModal() {
      if (this.isBusy) return;
      this.$refs.visume_mode.hide();
    },
    openModal: async function (type = "") {
      this.open = type == "" ? this.open : type;
      if (this.open == "visume") {
        this.$refs["visume_mode"].show();
        if(this.visume){
          this.videoResume = true;
          this.videoView = false;
          this.videoPreview = false;
          this.updateVisume = true;
          this.startRecording = false;
        }else{
          this.videoPreview = false;
          this.videoView = true;
          this.videoResume = false;
          this.startRecording = true;
          this.updateVisume = false;
        }
      }
    },
    stopMediaStream() {
      const activeStream = window.stream || this.stream;
      if (activeStream && typeof activeStream.getTracks === 'function') {
        activeStream.getTracks().forEach((track) => track.stop());
      }
      window.stream = null;
      this.stream = '';
      this.streamActive = false;
      this.mediaRecorder = '';
      this.hasWorkingDevices = false;
    },
    resetModal() {
      this.stopMediaStream();
      this.permissionGuideOpen = false;
      this.addVideo = false;
      this.checkRecord = false;
      this.stopRecord = false;
      this.recordedBlobs = [];
      this.checkRecording = false;
      this.OverAllVideoCoundownTimer = "";
      this.sec = 0;
      this.min = 0;
      this.timer_is_on = 0;
      this.data.resume = '';
      this.waiting = false;
      this.saveStatus = 'idle';
      this.uploadPhase = '';
      this.savedDuration = 0;
      this.clearSuccessReloadTimer();
      this.visume_error = false;
      this.visume_err_msg = null;

      if (this.visume) {
        this.videoResume = true;
        this.videoView = false;
        this.videoPreview = false;
        this.updateVisume = true;
        this.startRecording = false;
      } else {
        this.videoPreview = false;
        this.videoView = true;
        this.videoResume = false;
        this.startRecording = true;
        this.updateVisume = false;
      }
    },
    closeModal(){
      if(this.$route.path.includes('/jobseeker/register')){
        window.location.href = '/jobseeker/login';
      }else{
        location.reload();
      }
    },
    hideErroModal() {
      this.permissionGuideOpen = false;
      this.$refs['camera-access-error'].hide();
    },
    retryCameraAccess() {
      this.permissionGuideOpen = false;
      this.$refs['camera-access-error'].hide();
      this.visume_error = false;
      this.visume_err_msg = null;
      this.initCamera();
    },
    showCameraPermissionGuide() {
      if (this.permissionGuideOpen) return;
      this.permissionGuideOpen = true;
      this.$refs['camera-access-error'].show();
    },
    Updating(){
      this.videoPreview = false;
      this.videoView = true;
      this.videoResume = false;
      this.startRecording = true;
      this.updateVisume = false;
      this.visume_error = false;
      this.visume_err_msg = null;
      this.stopMediaStream();
    },
    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },
    handleSubmit() {
      if (!this.checkFormValidity()) {
        return;
      }
      this.$nextTick(() => {
        this.$bvModal.hide("modal-prevent-closing");
      });
    },
    async initCamera() {
      const blockReason = this.getRecordingBlockReason();
      if (blockReason) {
        this.hasWorkingDevices = false;
        this.streamActive = false;
        this.setRecordingError(blockReason);
        return;
      }
      this.stopMediaStream();
      try {
        const stream = await navigator.mediaDevices.getUserMedia(this.constrains);
        await this.attachStream(stream);
      } catch (err) {
        console.log(err);
        this.hasWorkingDevices = false;
        this.streamActive = false;
        this.handleMediaSupportError(err);
      }
    },
    async attachStream(stream) {
      window.stream = stream;
      this.stream = stream;
      this.hasWorkingDevices = true;
      this.streamActive = true;

      await this.$nextTick();
      const video = document.getElementById('video_view');
      if (video) {
        video.srcObject = stream;
        try {
          await video.play();
        } catch (err) {
          console.log('Video preview autoplay blocked:', err);
        }
      }
      this.startMediaRecorder();
    },
    getRecordingBlockReason() {
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        return 'Camera recording needs a secure connection (HTTPS). For local testing, use http://localhost:8080 instead of an IP address, or deploy with HTTPS.';
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        return 'Your browser does not expose camera access here. Please use the latest Chrome or Safari.';
      }
      if (!window.MediaRecorder) {
        return 'Video recording is not available in this browser. Please use the latest Chrome or Safari.';
      }
      return '';
    },
    setRecordingError(message = '') {
      const fallback = message || this.getRecordingBlockReason() || 'Could not start recording. Please try again.';
      this.visume_error = true;
      this.visume_err_msg = fallback;
    },
    notsupportingRecording() {
      this.setRecordingError();
    },
    handleMediaSupportError(err) {
      console.log(err);
      this.visume_error = true;
      const denied = err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
      if (denied) {
        this.visume_err_msg = 'Camera access was blocked. Allow camera and microphone, then tap Start recording again.';
        this.showCameraPermissionGuide();
      } else if (err && err.name === 'NotFoundError') {
        this.visume_err_msg = 'No camera or microphone was found on this device.';
      } else if (err && err.name === 'NotReadableError') {
        this.visume_err_msg = 'Camera is in use by another app. Close it and try again.';
      } else {
        this.visume_err_msg = 'Could not access your camera. Please try again.';
      }
    },
    handleAvalibleData: function (e) {
      if (e.data && e.data.size > 0) {
        this.recordedBlobs.push(e.data);
      }
    },
    handleStart: function () {
      this.recordedBlobs = [];
      this.checkRecording = false;
      if (this.mediaRecorder.state == "recording") {
        this.video_start_time = Date.now();
        console.log("recording");
      } else {
        console.log("error");
      }
    },
    overAllVideoCoundown: function () {
      var self = this;
      ++this.sec;
      if (this.sec == 59) {
        this.sec = 0;
        this.min = parseInt(this.min) + 1;
      }
      var pat = /^[0-9]{1}$/;
      this.sec = pat.test(this.sec) == true ? "0" + this.sec : this.sec;
      this.min = pat.test(this.min) == true ? "0" + this.min : this.min;
      document.getElementById("video_min").innerHTML = this.min;
      document.getElementById("video_sec").innerHTML = this.sec;
      if (this.min == "02") {
        this.stopRecording();
        return;
      }
      this.OverAllVideoCoundownTimer = setTimeout(function () {
        self.overAllVideoCoundown();
      }, 1000);
    },
    startVideoCount: function () {
      if (!this.timer_is_on) {
        this.timer_is_on = 1;
        this.overAllVideoCoundown();
      }
    },
    stopVideoCount: function () {
      clearInterval(this.OverAllVideoCoundownTimer);
      this.timer_is_on = 0;
    },
    handleStop: function () {
      this.stopVideoCount();
      this.video_end_time = Date.now();
      this.uploadFile(this.recordedBlobs);
      this.videoPreview = true;
      this.videoView = false;
      this.startPreviewVideo();
    },
    startPreviewVideo: function() {
      var superBuffer = new Blob(this.recordedBlobs, {type: 'video/mp4'});
      var src = window.URL.createObjectURL(superBuffer);
      document.getElementById("video_preview").removeAttribute('src');
      document.getElementById("video_preview").src = src;
    },
    checkDevicesWroking() {
      this.initCamera();
    },
    recordVisume: async function () {
      this.stream = navigator.mediaDevices.getUserMedia({
        video: {
          minAspectRatio: 1.333,
          minFrameRate: 60,
          width: 640,
          heigth: 480,
        },
        audio: true,
      });
      document.querySelector("video").srcObject = this.stream;
      if (this.stream.active) {
        this.streamActive = true;
      }
    },
    getCount(limit) {
      const countdownRoot = document.getElementById('countdown');
      const countdownNumberEl = document.getElementById('countdown-number');
      const countdownSvg = countdownRoot ? countdownRoot.querySelector('svg') : null;
      if (!countdownNumberEl) {
        return Promise.resolve('done');
      }
      let countdown = limit;
      countdownNumberEl.textContent = countdown;
      countdownNumberEl.style.display = 'block';
      if (countdownSvg) {
        countdownSvg.style.display = 'block';
      }
      return new Promise((resolve) => {
        const countInterval = setInterval(() => {
          if (countdown <= 0) {
            clearInterval(countInterval);
            if (countdownSvg) {
              countdownSvg.style.display = 'none';
            }
            countdownNumberEl.style.display = 'none';
            resolve('done');
            return;
          }
          countdown -= 1;
          countdownNumberEl.textContent = countdown;
        }, 600);
      });
    },
    startMediaRecorder() {
      if (!this.hasWorkingDevices || !window.stream) {
        this.hasWorkingDevices = false;
        return;
      }
      const blockReason = this.getRecordingBlockReason();
      if (blockReason) {
        this.hasWorkingDevices = false;
        this.setRecordingError(blockReason);
        return;
      }
      const mimeTypes = [
        'video/webm;codecs=opus,vp8',
        'video/webm;codecs=vp8',
        'video/webm',
        '',
      ];
      const mimeType = mimeTypes.find((type) => !type || MediaRecorder.isTypeSupported(type));
      this.options = { mimeType: mimeType || '' };
      try {
        this.mediaRecorder = new MediaRecorder(window.stream, this.options);
        this.mediaRecorder.ondataavailable = this.handleAvalibleData;
        this.mediaRecorder.onstart = this.handleStart;
        this.mediaRecorder.onstop = this.handleStop;
      } catch (e) {
        console.log(e);
        this.mediaRecorder = '';
        this.setRecordingError('Could not start the recorder. Please reload and try again.');
      }
    },
    startRecord: async function () {
      this.visume_error = false;
      this.visume_err_msg = null;

      const blockReason = this.getRecordingBlockReason();
      if (blockReason) {
        this.setRecordingError(blockReason);
        return;
      }

      if (!this.streamActive || !this.mediaRecorder) {
        await this.initCamera();
      }
      if (!this.streamActive || !this.mediaRecorder) {
        return;
      }

      this.startRecording = false;
      this.checkRecord = false;
      this.addVideo = false;
      this.stopRecord = true;

      const res = await this.getCount(5);
      if (res === 'done' && this.hasWorkingDevices && this.mediaRecorder) {
        this.sec = 0;
        this.min = 0;
        const timerEl = document.getElementById('time-interval');
        if (timerEl) {
          timerEl.style.display = 'flex';
        }
        this.startVideoCount();
        if (this.mediaRecorder.state !== 'recording') {
          this.mediaRecorder.start(10);
        }
      }
    },
    stopRecording() {
      if (this.hasWorkingDevices && this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.stopRecord = false;
        this.waiting = true;
        this.handleStop();
      }
    },
    async uploadFile(blob) {
      const file = new Blob(blob, { type: "application/octet-binary" });
      const awsUploader = new AwsUpload();
      this.uploadPhase = 'uploading';
      if (file.size >= 20000000) {
        this.setSaveError('Video file is too large (max 20 MB). Record a shorter clip and try again.');
        return;
      }
      try {
          var d = new Date();
          var month = d.getMonth() + 1;
          var day = d.getDate();
          var current_date =d.getFullYear() +"/" +(month < 10 ? "0" : "") +month +"/" +(day < 10 ? "0" : "") +day;
          var timestamp = Math.floor(Date.now() / 1000);
          var filename = 'visume/'+ current_date+"/profile_visume"+ timestamp + ".mp4";
          const response = await awsUploader.uploadToS3(file, filename);
          console.log(response);
          this.data.resume = response.url;
          this.uploadPhase = 'saving';
          this.saveRecord();
        } catch (error) {
          console.error('Failed to upload file to S3:', error);
          this.setSaveError('Upload failed. Check your connection and try again.');
        }
    },
    recordAgain(){
      this.startRecording = true
      this.addVideo = false,
      this.checkRecord = false,
      this.stopRecord = false,
      this.videoView = true,
      this.videoPreview = false,
      this.recordedBlobs=[],
      this.checkRecording= false,
      this.OverAllVideoCoundownTimer="",
      this.sec= 0,
      this.min=0,
      this.timer_is_on=0;
    
      var pat = /^[0-9]{1}$/;
      this.sec = pat.test(this.sec) == true ? "0" + this.sec : this.sec;
      this.min = pat.test(this.min) == true ? "0" + this.min : this.min;
      document.getElementById("video_min").innerHTML = this.min;
      document.getElementById("video_sec").innerHTML = this.sec;
    },
   saveRecord() {
    this.duration = Math.round((this.video_end_time - this.video_start_time)/1000);
    
    if(this.$route.path.includes('/jobseeker/register') && this.$cookies.get("seeker")){
        let data = {resume:this.data.resume,duration: this.duration,seeker_id:this.$cookies.get("seeker")}
        this.apiAction.uploadVisumeID(data).then(res => {
        if(res.message == 'Resume Updated'){
          setVisumeFeedback({ type: 'success', duration: this.duration });
          this.$cookies.remove("seeker");
          if (window.stream) {
            const tracks = window.stream.getVideoTracks();
            tracks.forEach(track => track.stop());
          }
          if(this.$route.query.src){
            this.$router.replace({name:'JobseekerSign',params:{visume:'uploaded',email_status:this.email_status==4?false:true},query:this.$route.query});
          }else{
            this.$router.replace({name:'JobseekerSign',params:{visume:'uploaded',email_status:this.email_status==4?false:true}});
          }
        }else{
          this.setSaveError(this.formatSaveError(res));
        }
      }).catch(err => {
        console.log(err);
        this.setSaveError(this.formatSaveError(null, err));
      })
    }else{
      let data = {resume:this.data.resume,duration: this.duration,token:this.$cookies.get("hire-seeker").token}
      this.apiAction.uploadVisume(data).then(res => {
        if(res.message == 'Resume Updated'){
          const seeker = {token: this.$cookies.get("hire-seeker").token,name:this.$cookies.get("hire-seeker").name,visume_path:this.data.resume,resume_path:this.$cookies.get("hire-seeker").resume_path,alert:this.$cookies.get("hire-seeker").alert}
          this.$cookies.set("hire-seeker", seeker);
          this.handleSaveSuccess();
        }else{
          this.setSaveError(this.formatSaveError(res));
        }
      }).catch(err => {
        console.log(err);
        this.setSaveError(this.formatSaveError(null, err));
      })
     }
    },
    showConfirmation(e){
      e.preventDefault();
      this.$refs['confirmation-visume'].show();
    },
    hideModal(){
      this.$refs['confirmation-visume'].hide();
    },
    skipVisume(e){
      e.preventDefault();
      if(window.stream){
        const tracks = window.stream.getVideoTracks();
        tracks.forEach(track => track.stop())
      }
      
      if(Object.values(this.$route.query).indexOf('test1')>-1) {
            this.$router.replace({name:'JobseekerSign',params:{visume:'uploaded',email_status:this.email_status==4?false:true},query:this.$route.query});
      }else if(this.$route.query.src){
        this.$router.replace({name:'JobseekerSign',params:{visume:'skipped',email_status:this.email_status==4?false:true},query:this.$route.query});
      }else{
        this.$router.push({name:'JobseekerSign',params:{visume:'skipped',email_status:this.email_status==4?false:true},query:this.$route.query});
      }
    }
  },
  watch: {
    open: {
      handler() {
        this.openModal();
      },
      deep: true,
    },
  },
  mounted() {
    this.$root.$on("bv::modal::hide", () => {
      this.open = "";
    });
  }
};
</script>
